import { getCenter, IRect } from './GeometryUtils';

const ALIGN_PADDING = 50;

export interface IAlignmentResult<T> {
  item: T;
  left: number;
  top: number;
}

/**
 * Aligns elements to the left
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignLeft<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignX({
    items,
    getBounds,
    pivotInitial: () => Number.MAX_VALUE,
    pivotComputation: (bounds, pivot) => Math.min(bounds.left, pivot),
    pivotFinal: (pivot) => pivot,
    alignComputation: (pivot) => pivot,
  });
}

/**
 * Aligns elements to the right
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignRight<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignX({
    items,
    getBounds,
    pivotInitial: () => Number.MIN_VALUE,
    pivotComputation: (bounds, pivot) => Math.max(/*right */bounds.left + bounds.width, pivot),
    pivotFinal: (pivot) => pivot,
    alignComputation: (pivot, target) => pivot - target.bounds.width,
  });
}

/**
 * Aligns elements to the center horizontally
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignCenterHorizontally<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignX({
    items,
    getBounds,
    pivotInitial: () => 0,
    pivotComputation: (bounds, pivot) => pivot + getCenter(bounds).x,
    pivotFinal: (pivot, count) => pivot / count,
    alignComputation: (pivot, target) => pivot - target.bounds.width / 2
  });
}

/**
 * Aligns elements to the top
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignTop<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignY({
    items,
    getBounds,
    pivotInitial: () => Number.MAX_VALUE,
    pivotComputation: (bounds, pivot) => Math.min(bounds.top, pivot),
    pivotFinal: (pivot) => pivot,
    alignComputation: (pivot) => pivot
  });
}

/**
 * Aligns elements to the bottom
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignBottom<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignY({
    items,
    getBounds,
    pivotInitial: () => Number.MIN_VALUE,
    pivotComputation: (bounds, pivot) => Math.max(bounds.top + bounds.height, pivot),
    pivotFinal: (pivot) => pivot,
    alignComputation: (pivot, target) => pivot - target.bounds.height
  });
}

/**
 * Aligns elements to center vertically
 * @param items items which have size and position for alignment
 * @param getBounds returns bounding rect for every item
 */
export function alignCenterVertically<T>(items: T[], getBounds: (item: T) => IRect): IAlignmentResult<T>[] {
  return alignY({
    items,
    getBounds,
    pivotInitial: () => 0,
    pivotComputation: (bounds, pivot) => pivot + getCenter(bounds).y,
    pivotFinal: (pivot, count) => pivot / count,
    alignComputation: (pivot, target) => pivot - target.bounds.height / 2
  });
}

interface IAlignParams<T> extends IAlignInfoParams<T> {
  alignComputation: (pivot: number, target: IAlignItemInfo<T>) => number;
}

function alignX<T>({ alignComputation, ...alignInfoProps }: IAlignParams<T>) {
  const align = getAlignInfo(alignInfoProps);
  let { boundingTop: top } = align;

  return align.items
    .sort((a, b) => (a.bounds.top - b.bounds.top))
    .map(x => {
      const newTop = Math.max(x.bounds.top, top);
      const newLeft = alignComputation(align.pivot, x);

      top = newTop + x.bounds.height + ALIGN_PADDING;

      return {
        item: x.item,
        top: newTop,
        left: newLeft,
      };
    });
}

function alignY<T>({ alignComputation, ...alignInfoProps }: IAlignParams<T>) {
  const align = getAlignInfo(alignInfoProps);
  let { boundingLeft: left } = align;

  return align.items
    .sort((a, b) => (a.bounds.left - b.bounds.left))
    .map(item => {
      const newLeft = Math.max(item.bounds.left, left);
      const newTop = alignComputation(align.pivot, item);

      left = newLeft + item.bounds.width + ALIGN_PADDING;

      return {
        item: item.item,
        top: newTop,
        left: newLeft,
      };
    });
}

interface IAlignItemInfo<T> {
  item: T;
  bounds: IRect;
}

interface IAlignInfo<T> {
  /**
   * Alignment line for either x or y axises.
   */
  pivot: number,
  boundingTop: number;
  boundingLeft: number;
  items: IAlignItemInfo<T>[];
}

interface IAlignInfoParams<T> {
  items: T[];
  getBounds: (item: T) => IRect;
  /**
   * Initial vallue for alignment line
   */
  pivotInitial: () => number;
  /**
   * Gets previos pivot value and returns new one
   */
  pivotComputation: (bounds: IRect, pivot: number) => number;
  /**
   * Makes final changes to the pivot after computation 
   */
  pivotFinal: (pivot: number, count: number) => number;
}

function getAlignInfo<T>({ items, getBounds, pivotInitial, pivotComputation, pivotFinal }: IAlignInfoParams<T>): IAlignInfo<T> {
  let pivot = pivotInitial ? pivotInitial() : 0;
  let left = Number.MAX_VALUE;
  let top = Number.MAX_VALUE;

  const stuff = items.map(x => {
    const bounds = getBounds(x);
    pivot = pivotComputation(bounds, pivot);

    left = Math.min(left, bounds.left);
    top = Math.min(top, bounds.top);

    return {
      item: x,
      bounds: bounds,
    };
  });

  pivotFinal && (pivot = pivotFinal(pivot, stuff.length));

  return {
    items: stuff,
    pivot,
    boundingTop: top,
    boundingLeft: left
  }
}
